import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User, Partner, PartnerAdmin, Advertisement, MarketingCampaign, PartnerCampaign, PromoCodeRequest, ContactInquiry, Category, Filter, AuthUser } from '../types';
import { notificationService } from '../services/notificationService';
import { PocketBaseAuthService as AuthService } from '../services/pocketbaseAuthService';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { loggingService } from '../services/loggingService';
import { notificationTriggerService } from '../services/notificationTriggerService';
import { activationService } from '../services/activationService';
import { debugLog } from '../services/debugConfig';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  loading: boolean;
  partners: Partner[];
  partnerAdmins: PartnerAdmin[];
  advertisements: Advertisement[];
  partnerCampaigns: PartnerCampaign[];
  marketingCampaigns: MarketingCampaign[];
  promoCodeRequests: PromoCodeRequest[];
  contactInquiries: ContactInquiry[];
  categories: Category[];
  filters: Filter[];
  createPartner: (partnerData: Omit<Partner, 'id' | 'created_at' | 'campaigns_count'>, logoFile?: File | null) => Promise<void>;
  updatePartner: (id: string, updates: Partial<Partner>, logoFile?: File | null) => Promise<void>;
  deletePartner: (id: string) => Promise<void>;
  createPartnerAdmin: (adminData: Omit<PartnerAdmin, 'id' | 'created_at'> & { password?: string; initial_password?: string }) => Promise<void>;
  updatePartnerAdmin: (id: string, updates: Partial<PartnerAdmin>) => Promise<void>;
  deletePartnerAdmin: (id: string) => Promise<void>;
  createAdvertisement: (adData: Omit<Advertisement, 'id' | 'created_at'>, bannerFile?: File | null) => Promise<Advertisement | null>;
  updateAdvertisement: (id: string, updates: Partial<Advertisement>, bannerFile?: File | null) => Promise<void>;
  deleteAdvertisement: (id: string) => Promise<void>;
  createPartnerCampaign: (campaignData: Omit<PartnerCampaign, 'id' | 'created_at'>) => Promise<void>;
  updatePartnerCampaign: (id: string, updates: Partial<PartnerCampaign>) => Promise<void>;
  deletePartnerCampaign: (id: string) => Promise<void>;
  createMarketingCampaign: (campaignData: Omit<MarketingCampaign, 'id' | 'created_at'>) => Promise<void>;
  updateMarketingCampaign: (id: string, updates: Partial<MarketingCampaign>) => Promise<void>;
  deleteMarketingCampaign: (id: string) => Promise<void>;
  submitPromoCodeRequest: (requestData: Omit<PromoCodeRequest, 'id' | 'requested_at'>) => Promise<void>;
  updatePromoCodeRequest: (id: string, updates: Partial<PromoCodeRequest>) => Promise<void>;
  createContactInquiry: (inquiryData: Omit<ContactInquiry, 'id' | 'created_at' | 'updated_at' | 'status' | 'priority'>) => Promise<void>;
  updateContactInquiry: (id: string, updates: Partial<ContactInquiry>) => Promise<void>;
  deleteContactInquiry: (id: string) => Promise<void>;
  createCategory: (categoryData: Omit<Category, 'id' | 'created_at'>) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  createFilter: (filterData: Omit<Filter, 'id' | 'created_at'>) => Promise<void>;
  updateFilter: (id: string, updates: Partial<Filter>) => Promise<void>;
  deleteFilter: (id: string) => Promise<void>;
  register: (userData: {
    name: string;
    email: string;
    password: string;
    role: 'admin' | 'partner';
    company_name?: string;
  }) => Promise<boolean>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  updateProfile: (updates: { name?: string; last_login?: string }) => Promise<void>;
  needsPasswordChange: boolean;
  approveAdvertisement: (id: string, approved_by: string) => Promise<void>;
  rejectAdvertisement: (id: string, approved_by: string, rejection_reason: string) => Promise<void>;
  changeAdvertisementStatus: (id: string, status: 'pending' | 'approved' | 'rejected', approved_by?: string, rejection_reason?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const loginInProgressRef = useRef(false);
  
  // Data states
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerAdmins, setPartnerAdmins] = useState<PartnerAdmin[]>([]);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [partnerCampaigns, setPartnerCampaigns] = useState<PartnerCampaign[]>([]);
  const [marketingCampaigns, setMarketingCampaigns] = useState<MarketingCampaign[]>([]);
  const [promoCodeRequests, setPromoCodeRequests] = useState<PromoCodeRequest[]>([]);
  const [contactInquiries, setContactInquiries] = useState<ContactInquiry[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);

  // Convert AuthUser to User type
  const convertAuthUserToUser = (authUser: AuthUser): User => ({
    id: authUser.id,
    email: authUser.email,
    role: authUser.role,
    name: authUser.name,
    created_at: authUser.created_at || new Date().toISOString(),
    partner_id: authUser.partner_id,
    last_login: authUser.last_login
  });

  // Load data needed for public routes (partners, campaigns, ads, categories)
  const loadPublicData = async () => {
    try {
      await debugLog('Loading public data...');
      
      const [
        partnersResult,
        advertisementsResult,
        partnerCampaignsResult,
        marketingCampaignsResult,
        categoriesResult,
        filtersResult
      ] = await Promise.all([
        DataService.getPartners(),
        DataService.getAdvertisements(),
        DataService.getPartnerCampaigns(),
        DataService.getMarketingCampaigns(),
        DataService.getCategories(),
        DataService.getFilters()
      ]);

      await debugLog('Public data results:', {
        partners: partnersResult.data?.length || 0,
        advertisements: advertisementsResult.data?.length || 0,
        partnerCampaigns: partnerCampaignsResult.data?.length || 0,
        marketingCampaigns: marketingCampaignsResult.data?.length || 0,
        categories: categoriesResult.data?.length || 0,
        filters: filtersResult.data?.length || 0
      });
      
      // Debug: Show all advertisements with partner IDs
      await debugLog('🔍 All advertisements loaded:', (advertisementsResult.data || []).map(ad => ({
        id: ad.id,
        name: ad.name,
        partner_id: ad.partner_id,
        status: ad.status,
        priority: ad.priority
      })));

      setPartners(partnersResult.data || []);
      setAdvertisements(advertisementsResult.data || []);
      setPartnerCampaigns(partnerCampaignsResult.data || []);
      setMarketingCampaigns(marketingCampaignsResult.data || []);
      setCategories(categoriesResult.data || []);
      setFilters(filtersResult.data || []);

      if (partnersResult.error) console.error('Partners error:', partnersResult.error);
      if (advertisementsResult.error) console.error('Advertisements error:', advertisementsResult.error);
      if (partnerCampaignsResult.error) console.error('Partner campaigns error:', partnerCampaignsResult.error);
      if (marketingCampaignsResult.error) console.error('Marketing campaigns error:', marketingCampaignsResult.error);
      if (categoriesResult.error) console.error('Categories error:', categoriesResult.error);
      if (filtersResult.error) console.error('Filters error:', filtersResult.error);

      await debugLog('Public data loading complete');
    } catch (error) {
      console.error('Failed to load public data:', error);
      setPartners([]);
      setAdvertisements([]);
      setPartnerCampaigns([]);
      setMarketingCampaigns([]);
      setCategories([]);
      setFilters([]);
    }
  };

  const loadAllData = async () => {
    await loadPublicData();
    await loadPrivateData();
  };

  // Load admin-only data (promo requests, contact inquiries, partner admins)
  const loadPrivateData = async () => {
    try {
      await debugLog('Loading private/admin data...');
      
      const [
        partnerAdminsResult,
        promoRequestsResult,
        contactInquiriesResult
      ] = await Promise.all([
        DataService.getPartnerAdmins(),
        DataService.getPromoRequests(),
        DataService.getContactInquiries()
      ]);

      await debugLog('Private data results:', {
        partnerAdmins: partnerAdminsResult.data?.length || 0,
        promoRequests: promoRequestsResult.data?.length || 0,
        contactInquiries: contactInquiriesResult.data?.length || 0
      });

      setPartnerAdmins(partnerAdminsResult.data || []);
      setPromoCodeRequests(promoRequestsResult.data || []);
      setContactInquiries(contactInquiriesResult.data || []);

      if (partnerAdminsResult.error) console.error('Partner admins error:', partnerAdminsResult.error);
      if (promoRequestsResult.error) console.error('Promo requests error:', promoRequestsResult.error);
      if (contactInquiriesResult.error) console.error('Contact inquiries error:', contactInquiriesResult.error);

      await debugLog('Private data loading complete');
    } catch (error) {
      console.error('Failed to load private data:', error);
      setPartnerAdmins([]);
      setPromoCodeRequests([]);
      setContactInquiries([]);
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await debugLog('Initializing auth...');
        
        const { user: authUser, error } = await AuthService.getCurrentUser();
        
        if (error) {
          await debugLog('No authenticated user:', error);
          setUser(null);
          await loadPublicData();
        } else if (authUser) {
          await debugLog('Found authenticated user:', authUser.email);
          setUser(convertAuthUserToUser(authUser));
          await loadPublicData();
          await loadPrivateData();
        } else {
          await debugLog('No user found');
          setUser(null);
          await loadPublicData();
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setUser(null);
        await loadPublicData();
      } finally {
        // Set loading to false only after data is loaded
        setLoading(false);
        await debugLog('Auth initialization complete');
      }
    };

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Auth initialization timeout - forcing loading to false');
      setLoading(false);
    }, 30000); // 30 second timeout

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });

    // Listen for auth changes
    const { data: { subscription } } = AuthService.onAuthStateChange(async (authUser) => {
      await debugLog('Auth state changed:', authUser?.email || 'logged out');
      
      if (authUser) {
        await debugLog('Session refreshed, updating user data');
        setUser(convertAuthUserToUser(authUser));
        // Don't reload all data on every auth state change, only on initial login
        // await loadData();
      } else {
        await debugLog('Session expired or user logged out');
        setUser(null);
        // Don't clear public data - public campaigns should still be viewable
        // Only clear sensitive user data
        setPartnerAdmins([]);
        setPromoCodeRequests([]);
        setContactInquiries([]);
        // Keep public data: partners, advertisements, campaigns, categories
      }
      setLoading(false);
    });

    return () => {
      void debugLog('Cleaning up auth subscription');
      subscription?.unsubscribe();
    };
  }, []);

  // Authentication functions
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (loginInProgressRef.current) {
        return { success: false, error: 'Login already in progress' };
      }

      loginInProgressRef.current = true;
      setLoading(true);
      const { user: authUser, error } = await AuthService.signIn(email, password);
      
      if (error || !authUser) {
        console.error('Login failed:', error);
        return { success: false, error: error || 'Login failed' };
      }

      const userData = convertAuthUserToUser(authUser);
      setUser(userData);
      
      // Log successful login
      await loggingService.logLogin(
        userData.id,
        userData.name,
        userData.role as 'global_admin' | 'partner_admin',
        userData.partner_id
      );
      
      // Check if user needs to change password (first login)
      const needsChange = authUser.first_login_completed === false;
      await debugLog('User first_login_completed status', {
        first_login_completed: authUser.first_login_completed,
        needsChange
      });
      setNeedsPasswordChange(needsChange);
      
      if (!needsChange) {
        await loadPublicData();
        await loadPrivateData();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const message =
        (error as any)?.data?.message ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'An unexpected error occurred';
      return { success: false, error: message };
    } finally {
      loginInProgressRef.current = false;
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      
      // Log logout before clearing user
      if (user) {
        await loggingService.logLogout(
          user.id,
          user.name,
          user.role as 'global_admin' | 'partner_admin',
          user.partner_id
        );
      }
      
      await AuthService.signOut();
      setUser(null);
      // Clear all data
      setPartners([]);
      setPartnerAdmins([]);
      setAdvertisements([]);
      setPartnerCampaigns([]);
      setMarketingCampaigns([]);
      setPromoCodeRequests([]);
      setContactInquiries([]);
      setCategories([]);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
    role: 'admin' | 'partner';
    company_name?: string;
  }): Promise<boolean> => {
    try {
      setLoading(true);
      const { user: authUser, error } = await AuthService.signUp(
        userData.email,
        userData.password,
        {
          name: userData.name,
          role: userData.role === 'admin' ? 'global_admin' : 'partner_admin'
        }
      );

      if (error || !authUser) {
        console.error('Registration failed:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }

      await debugLog('Changing password for user:', user.email);

      // For initial password change, we need to handle it differently
      const isInitialPasswordChange = needsPasswordChange;
      
      // For auth collections, we need to update both password and first_login_completed
      // PocketBase requires passwordConfirm to match password AND oldPassword for security
      const updateData: any = {
        oldPassword: oldPassword,
        password: newPassword,
        passwordConfirm: newPassword
      };

      // Only include these fields if this is an initial password change
      if (isInitialPasswordChange) {
        updateData.first_login_completed = true;
        updateData.is_activated = true;
        updateData.status = 'active';
      }

      await debugLog('Updating user with:', { 
        ...updateData, 
        oldPassword: '***', 
        password: '***', 
        passwordConfirm: '***',
        isInitialPasswordChange
      });

      const result = await DataService.updateUser(user.id, updateData);
      
      if (result.error) {
        console.error('Update error:', result.error);
        throw new Error(result.error);
      }

      await debugLog('Password changed successfully');
      
      await loggingService.logPasswordChange(
        user.id,
        user.name,
        user.role as 'global_admin' | 'partner_admin',
        user.partner_id || undefined,
        true
      );

      // After password change, force logout to get a fresh auth token.
      // This also ensures first-login users must re-authenticate with their new credentials.
      await debugLog(
        isInitialPasswordChange
          ? 'First-login password change complete - logging out for fresh authentication'
          : 'Regular password change complete - logging out for fresh authentication'
      );
      
      // Clear local state
      setUser(null);
      setNeedsPasswordChange(false);
      
      // Sign out from PocketBase to clear stale token
      await AuthService.signOut();
      
      // Clear all cached data
      setPartners([]);
      setPartnerAdmins([]);
      setAdvertisements([]);
      setPartnerCampaigns([]);
      setMarketingCampaigns([]);
      setPromoCodeRequests([]);
      setContactInquiries([]);
      setCategories([]);
    } catch (error: any) {
      console.error('Failed to change password:', error);

      if (user) {
        await loggingService.logPasswordChange(
          user.id,
          user.name,
          user.role as 'global_admin' | 'partner_admin',
          user.partner_id || undefined,
          false
        );
      }
      
      throw new Error(error.message || 'Failed to change password');
    }
  };

  const updateProfile = async (updates: { name?: string; last_login?: string }): Promise<void> => {
    if (!user) {
      throw new Error('No user logged in');
    }

    const normalizedUpdates: any = {};
    if (updates.name !== undefined) normalizedUpdates.name = updates.name;
    if (updates.last_login !== undefined) normalizedUpdates.last_login = updates.last_login;

    if (Object.keys(normalizedUpdates).length === 0) return;

    const result = await DataService.updateUser(user.id, normalizedUpdates);
    if (result.error) {
      throw new Error(result.error);
    }

    setUser(prev => prev ? { ...prev, ...normalizedUpdates } : prev);

    if (updates.name !== undefined) {
      await loggingService.logProfileUpdate(
        user.id,
        user.name,
        user.role as 'global_admin' | 'partner_admin',
        user.partner_id || undefined,
        { name: updates.name }
      );
    }
  };

  // Partner functions
  const createPartner = async (partnerData: Omit<Partner, 'id' | 'created_at' | 'campaigns_count'>, logoFile?: File | null): Promise<void> => {
    try {
      const { data, error } = await DataService.createPartner(partnerData, logoFile);
      if (error) throw new Error(error);
      if (data) {
        setPartners(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create partner:', error);
      throw error;
    }
  };

  const updatePartner = async (id: string, updates: Partial<Partner>, logoFile?: File | null): Promise<void> => {
    try {
      const { data, error } = await DataService.updatePartner(id, updates, logoFile);
      if (error) throw new Error(error);
      if (data) {
        setPartners(prev => prev.map(p => p.id === id ? data : p));
      }
    } catch (error) {
      console.error('Failed to update partner:', error);
      throw error;
    }
  };

  const deletePartner = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deletePartner(id);
      if (error) throw new Error(error);
      setPartners(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Failed to delete partner:', error);
      throw error;
    }
  };

  // Partner Admin functions
  const createPartnerAdmin = async (adminData: Omit<PartnerAdmin, 'id' | 'created_at'> & { password?: string; initial_password?: string }): Promise<void> => {
    try {
      await debugLog('Creating partner admin:', adminData);
      
      // Check activation method
      if (adminData.activation_method === 'email_link') {
        // Generate activation token first
        const tokenInfo = await activationService.generateActivationToken();
        
        // Email activation method - create user with activation token included
        // The PocketBase hook (user_activation.pb.js) will send the email server-side
        const userData = {
          name: adminData.name,
          email: adminData.email,
          role: 'partner_admin',
          partner_id: adminData.partner_id,
          first_login_completed: false,
          activation_method: 'email_link',
          is_activated: false,
          status: 'inactive',
          // Include activation token so the PocketBase hook can access it
          activation_token: tokenInfo.token,
          activation_token_expires: tokenInfo.expiresAt.toISOString()
        };
        
        // Use the activation token as the temporary password
        // This allows the user to "authenticate" with the token during activation
        const { error } = await AuthService.signUp(adminData.email, tokenInfo.token, userData);
        if (error) throw new Error(error);
        
        // Note: Email is now sent by PocketBase hook (user_activation.pb.js) server-side
        // This is more secure as the Brevo API key stays on the server
        await activationService.sendActivationEmail(
          adminData.email,
          adminData.name,
          tokenInfo.token
        );
        
        await debugLog('Partner admin created with email activation');
        await debugLog('Activation email will be sent by PocketBase hook');
      } else {
        // Password activation method (existing flow)
        const password = adminData.password || adminData.initial_password || '';
        
        if (!password) {
          throw new Error('Password is required for password activation method');
        }
        
        // Create user in users collection with partner_admin role
        const userData = {
          name: adminData.name,
          role: 'partner_admin',
          partner_id: adminData.partner_id,
          first_login_completed: false,
          activation_method: 'password',
          is_activated: false,
          initial_password: password,
          status: 'inactive'
        };
        
        const { error } = await AuthService.signUp(adminData.email, password, userData);
        if (error) throw new Error(error);
        
        await debugLog('Partner admin created successfully');
        await debugLog('User must change password on first login');
      }
      
      // Reload data to get the new admin
      await loadAllData();
    } catch (error) {
      console.error('Failed to create partner admin:', error);
      throw error;
    }
  };

  const updatePartnerAdmin = async (id: string, updates: Partial<PartnerAdmin>): Promise<void> => {
    try {
      await debugLog('Updating partner admin:', { id, updates });
      
      // Update user in users collection
      const updateData: any = {};
      if (updates.name) updateData.name = updates.name;
      if (updates.email) updateData.email = updates.email;
      if (updates.status) updateData.status = updates.status;
      if (updates.partner_id) updateData.partner_id = updates.partner_id;
      
      await DataService.updateUser(id, updateData);
      
      // Reload data
      await loadPrivateData();
    } catch (error) {
      console.error('Failed to update partner admin:', error);
      throw error;
    }
  };

  const deletePartnerAdmin = async (id: string): Promise<void> => {
    try {
      await debugLog('Deleting partner admin:', id);
      await DataService.deleteUser(id);
      
      // Reload data
      await loadPrivateData();
    } catch (error) {
      console.error('Failed to delete partner admin:', error);
      throw error;
    }
  };

  // Advertisement functions
  const createAdvertisement = async (adData: Omit<Advertisement, 'id' | 'created_at'>, bannerFile?: File | null): Promise<Advertisement | null> => {
    try {
      const { data, error} = await DataService.createAdvertisement(adData, bannerFile);
      if (error) throw new Error(error);
      if (data) {
        setAdvertisements(prev => [data, ...prev]);
        
        // Log advertisement creation
        if (user) {
          await loggingService.logAdvertisementCreated(
            user.id,
            user.name,
            user.role as 'global_admin' | 'partner_admin',
            data.partner_id,
            data.id,
            data.name,
            {
              categories: data.categories,
              priority: data.priority,
              approval_status: data.approval_status
            }
          );
        }
        
        // Notify all global admins about new ad pending review
        try {
          const { data: globalAdmins } = await DataService.getGlobalAdmins();
          const partner = partners.find(p => p.id === data.partner_id);
          
          if (partner && globalAdmins && globalAdmins.length > 0) {
            await debugLog(`Notifying ${globalAdmins.length} global admins about new ad: ${data.name}`);
            for (const admin of globalAdmins) {
              await notificationService.notifyAdPendingReview(
                admin.id,
                data.name,
                data.id,
                partner.name
              );
              await debugLog('Notification sent to global admin:', admin.email);
            }
          } else {
            console.warn('⚠️ No global admins found or partner not found');
          }
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
          // Don't throw - notification failure shouldn't block ad creation
        }
        
        return data;
      }
    } catch (error) {
      console.error('Failed to create advertisement:', error);
      throw error;
    }
    return null;
  };

  const updateAdvertisement = async (id: string, updates: Partial<Advertisement>, bannerFile?: File | null): Promise<void> => {
    try {
      const originalAd = advertisements.find(ad => ad.id === id);
      const { data, error } = await DataService.updateAdvertisement(id, updates, bannerFile);
      if (error) throw new Error(error);
      if (data) {
        setAdvertisements(prev => prev.map(ad => ad.id === id ? data : ad));
        
        // Log advertisement update
        if (user) {
          const changes: Record<string, any> = {};
          if (originalAd) {
            Object.keys(updates).forEach(key => {
              const oldVal = (originalAd as any)[key];
              const newVal = (updates as any)[key];
              if (oldVal !== newVal) {
                changes[key] = { old: oldVal, new: newVal };
              }
            });
          }
          
          await loggingService.logAdvertisementUpdated(
            user.id,
            user.name,
            user.role as 'global_admin' | 'partner_admin',
            data.partner_id,
            data.id,
            data.name,
            changes
          );
        }
        
        // If ad status changed to pending (partner edited it), notify global admins
        if (updates.approval_status === 'pending' && originalAd?.approval_status !== 'pending') {
          try {
            const { data: globalAdmins } = await DataService.getGlobalAdmins();
            const partner = partners.find(p => p.id === data.partner_id);
            
            if (partner && globalAdmins && globalAdmins.length > 0) {
              const isResubmission = originalAd?.approval_status === 'rejected';
              
              await debugLog(
                `Notifying ${globalAdmins.length} global admins about ${isResubmission ? 'resubmitted' : 'updated'} ad: ${data.name}`
              );
              for (const admin of globalAdmins) {
                await notificationService.notifyAdPendingReview(
                  admin.id,
                  data.name,
                  data.id,
                  partner.name
                );
                await debugLog('Ad review notification sent to global admin:', admin.email);
              }
            } else {
              console.warn('⚠️ No global admins found or partner not found for ad update notification');
            }
          } catch (notifError) {
            console.error('Failed to send ad update notification:', notifError);
            // Don't throw - notification failure shouldn't block ad update
          }
        }
      }
    } catch (error) {
      console.error('Failed to update advertisement:', error);
      throw error;
    }
  };

  const deleteAdvertisement = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deleteAdvertisement(id);
      if (error) throw new Error(error);
      setAdvertisements(prev => prev.filter(ad => ad.id !== id));
    } catch (error) {
      console.error('Failed to delete advertisement:', error);
      throw error;
    }
  };

  // Partner Campaign functions
  const createPartnerCampaign = async (campaignData: Omit<PartnerCampaign, 'id' | 'created_at'>): Promise<void> => {
    try {
      const { data, error } = await DataService.createPartnerCampaign(campaignData);
      if (error) throw new Error(error);
      if (data) {
        setPartnerCampaigns(prev => [data, ...prev]);
        
        // Log campaign creation
        if (user) {
          await loggingService.logCampaignCreated(
            user.id,
            user.name,
            user.role as 'global_admin' | 'partner_admin',
            data.partner_id,
            data.id,
            data.name,
            {
              status: data.status,
              region: data.region
            }
          );
        }
      }
    } catch (error) {
      console.error('Failed to create partner campaign:', error);
      throw error;
    }
  };

  const updatePartnerCampaign = async (id: string, updates: Partial<PartnerCampaign>): Promise<void> => {
    try {
      const originalCampaign = partnerCampaigns.find(pc => pc.id === id);
      const { data, error } = await DataService.updatePartnerCampaign(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setPartnerCampaigns(prev => prev.map(pc => pc.id === id ? data : pc));
        
        // Log campaign update
        if (user) {
          // Check if status changed
          if (originalCampaign && updates.status && originalCampaign.status !== updates.status) {
            await loggingService.logCampaignStatusChanged(
              user.id,
              user.name,
              user.role as 'global_admin' | 'partner_admin',
              data.partner_id,
              data.id,
              data.name,
              originalCampaign.status,
              updates.status
            );
          } else {
            // General update
            const changes: Record<string, any> = {};
            if (originalCampaign) {
              Object.keys(updates).forEach(key => {
                const oldVal = (originalCampaign as any)[key];
                const newVal = (updates as any)[key];
                if (oldVal !== newVal) {
                  changes[key] = { old: oldVal, new: newVal };
                }
              });
            }
            
            await loggingService.logCampaignUpdated(
              user.id,
              user.name,
              user.role as 'global_admin' | 'partner_admin',
              data.partner_id,
              data.id,
              data.name,
              changes
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to update partner campaign:', error);
      throw error;
    }
  };

  const deletePartnerCampaign = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deletePartnerCampaign(id);
      if (error) throw new Error(error);
      setPartnerCampaigns(prev => prev.filter(pc => pc.id !== id));
    } catch (error) {
      console.error('Failed to delete partner campaign:', error);
      throw error;
    }
  };

  // Marketing Campaign functions
  const createMarketingCampaign = async (campaignData: Omit<MarketingCampaign, 'id' | 'created_at'>): Promise<void> => {
    try {
      const { data, error } = await DataService.createMarketingCampaign(campaignData);
      if (error) throw new Error(error);
      if (data) {
        setMarketingCampaigns(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create marketing campaign:', error);
      throw error;
    }
  };

  const updateMarketingCampaign = async (id: string, updates: Partial<MarketingCampaign>): Promise<void> => {
    try {
      const { data, error } = await DataService.updateMarketingCampaign(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setMarketingCampaigns(prev => prev.map(mc => mc.id === id ? data : mc));
      }
    } catch (error) {
      console.error('Failed to update marketing campaign:', error);
      throw error;
    }
  };

  const deleteMarketingCampaign = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deleteMarketingCampaign(id);
      if (error) throw new Error(error);
      setMarketingCampaigns(prev => prev.filter(mc => mc.id !== id));
    } catch (error) {
      console.error('Failed to delete marketing campaign:', error);
      throw error;
    }
  };

  // Promo Code Request functions
  const submitPromoCodeRequest = async (requestData: Omit<PromoCodeRequest, 'id' | 'requested_at'>): Promise<void> => {
    try {
      await debugLog('Submitting promo code request:', requestData);
      
      // Clean up the request data - pass through as-is, validation happens in service layer
      const cleanedData = {
        email: requestData.email,
        partner_campaign_id: requestData.partner_campaign_id,
        partner_id: requestData.partner_id,
        advertisement_id: requestData.advertisement_id,
        advertisement_name: requestData.advertisement_name,
        campaign_name: requestData.campaign_name,
        agreed_to_privacy: requestData.agreed_to_privacy,
        marketing_campaign_id: requestData.marketing_campaign_id,
        promo_code: requestData.promo_code,
        status: requestData.status
      };
      
      await debugLog('Promo code request cleaned data:', cleanedData);
      
      const { data, error } = await DataService.createPromoRequest(cleanedData);
      if (error) {
        console.error('❌ PocketBase error:', error);
        throw new Error(error);
      }
      if (data) {
        await debugLog('Promo request saved to database:', data);
        setPromoCodeRequests(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('❌ Failed to submit promo code request:', error);
      throw error;
    }
  };

  const updatePromoCodeRequest = async (id: string, updates: Partial<PromoCodeRequest>): Promise<void> => {
    try {
      const originalRequest = promoCodeRequests.find(req => req.id === id);
      const { data, error } = await DataService.updatePromoRequest(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setPromoCodeRequests(prev => prev.map(req => req.id === id ? data : req));
        
        // Log promo request status change
        if (user && originalRequest && updates.status && originalRequest.status !== updates.status) {
          // Find the partner_id from the advertisement
          const ad = advertisements.find(a => a.id === data.advertisement_id);
          const partnerId = ad?.partner_id || user.partner_id;
          
          await loggingService.logPromoRequestStatusChanged(
            user.id,
            user.name,
            user.role as 'global_admin' | 'partner_admin',
            partnerId || '',
            data.id,
            data.email,
            originalRequest.status,
            updates.status
          );
        }
      }
    } catch (error) {
      console.error('Failed to update promo code request:', error);
      throw error;
    }
  };

  // Contact Inquiry functions
  const createContactInquiry = async (inquiryData: Omit<ContactInquiry, 'id' | 'created_at' | 'updated_at' | 'status' | 'priority'>): Promise<void> => {
    try {
      const { data, error } = await DataService.createContactInquiry(inquiryData);
      if (error) throw new Error(error);
      if (data) {
        setContactInquiries(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create contact inquiry:', error);
      throw error;
    }
  };

  const updateContactInquiry = async (id: string, updates: Partial<ContactInquiry>): Promise<void> => {
    try {
      const { data, error } = await DataService.updateContactInquiry(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setContactInquiries(prev => prev.map(ci => ci.id === id ? data : ci));
      }
    } catch (error) {
      console.error('Failed to update contact inquiry:', error);
      throw error;
    }
  };

  const deleteContactInquiry = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deleteContactInquiry(id);
      if (error) throw new Error(error);
      setContactInquiries(prev => prev.filter(ci => ci.id !== id));
    } catch (error) {
      console.error('Failed to delete contact inquiry:', error);
      throw error;
    }
  };

  // Category functions
  const createCategory = async (categoryData: Omit<Category, 'id' | 'created_at'>): Promise<void> => {
    try {
      const { data, error } = await DataService.createCategory(categoryData);
      if (error) throw new Error(error);
      if (data) {
        setCategories(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>): Promise<void> => {
    try {
      const { data, error } = await DataService.updateCategory(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setCategories(prev => prev.map(c => c.id === id ? data : c));
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      throw error;
    }
  };

  const deleteCategory = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deleteCategory(id);
      if (error) throw new Error(error);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Failed to delete category:', error);
      throw error;
    }
  };

  // Filter functions
  const createFilter = async (filterData: Omit<Filter, 'id' | 'created_at'>): Promise<void> => {
    try {
      const { data, error } = await DataService.createFilter(filterData);
      if (error) throw new Error(error);
      if (data) {
        setFilters(prev => [data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create filter:', error);
      throw error;
    }
  };

  const updateFilter = async (id: string, updates: Partial<Filter>): Promise<void> => {
    try {
      const { data, error } = await DataService.updateFilter(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setFilters(prev => prev.map(f => f.id === id ? data : f));
      }
    } catch (error) {
      console.error('Failed to update filter:', error);
      throw error;
    }
  };

  const deleteFilter = async (id: string): Promise<void> => {
    try {
      const { error } = await DataService.deleteFilter(id);
      if (error) throw new Error(error);
      setFilters(prev => prev.filter(f => f.id !== id));
    } catch (error) {
      console.error('Failed to delete filter:', error);
      throw error;
    }
  };

  // Advertisement Approval functions
  const approveAdvertisement = async (id: string, approved_by: string): Promise<void> => {
    try {
      const ad = advertisements.find(a => a.id === id);
      const updates = {
        approval_status: 'approved' as const,
        approved_by,
        approved_at: new Date().toISOString()
      };
      
      const { data, error } = await DataService.updateAdvertisement(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setAdvertisements(prev => prev.map(ad => ad.id === id ? data : ad));
        
        // Trigger email notification for advertisement_approved
        if (ad) {
          notificationTriggerService.triggerEvent(
            ad.partner_id,
            'advertisement_approved',
            {
              ad_title: ad.name,
              ad_name: ad.name,
              ad_id: ad.id,
              approved_by: user?.name || 'Admin',
              approved_at: updates.approved_at,
              ad_url: `${window.location.origin}/advertisements/${ad.id}`
            }
          ).catch(err => console.error('Failed to trigger email notifications:', err));
        }
      }
    } catch (error) {
      console.error('Failed to approve advertisement:', error);
      throw error;
    }
  };

  const rejectAdvertisement = async (id: string, approved_by: string, rejection_reason: string): Promise<void> => {
    try {
      const ad = advertisements.find(a => a.id === id);
      const updates = {
        approval_status: 'rejected' as const,
        approved_by,
        approved_at: new Date().toISOString(),
        rejection_reason
      };
      
      const { data, error } = await DataService.updateAdvertisement(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setAdvertisements(prev => prev.map(ad => ad.id === id ? data : ad));
        
        // Trigger email notification for advertisement_rejected
        if (ad) {
          notificationTriggerService.triggerEvent(
            ad.partner_id,
            'advertisement_rejected',
            {
              ad_title: ad.name,
              ad_name: ad.name,
              ad_id: ad.id,
              rejected_by: user?.name || 'Admin',
              rejected_at: updates.approved_at,
              rejection_reason: rejection_reason,
              ad_url: `${window.location.origin}/advertisements/${ad.id}`
            }
          ).catch(err => console.error('Failed to trigger email notifications:', err));
        }
      }
    } catch (error) {
      console.error('Failed to reject advertisement:', error);
      throw error;
    }
  };

  const changeAdvertisementStatus = async (
    id: string,
    status: 'pending' | 'approved' | 'rejected',
    approved_by?: string,
    rejection_reason?: string
  ): Promise<void> => {
    try {
      const updates: any = {
        approval_status: status
      };

      if (status === 'pending') {
        // When returning to pending, clear approval data
        updates.approved_by = null;
        updates.approved_at = null;
        updates.rejection_reason = null;
      } else if (status === 'approved') {
        updates.approved_by = approved_by;
        updates.approved_at = new Date().toISOString();
        updates.rejection_reason = null;
      } else if (status === 'rejected') {
        updates.approved_by = approved_by;
        updates.approved_at = new Date().toISOString();
        updates.rejection_reason = rejection_reason;
      }

      const { data, error } = await DataService.updateAdvertisement(id, updates);
      if (error) throw new Error(error);
      if (data) {
        setAdvertisements(prev => prev.map(ad => ad.id === id ? data : ad));
      }
    } catch (error) {
      console.error('Failed to change advertisement status:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
    partners,
    partnerAdmins,
    advertisements,
    partnerCampaigns,
    marketingCampaigns,
    promoCodeRequests,
    contactInquiries,
    categories,
    filters,
    createPartner,
    updatePartner,
    deletePartner,
    createPartnerAdmin,
    updatePartnerAdmin,
    deletePartnerAdmin,
    createAdvertisement,
    updateAdvertisement,
    deleteAdvertisement,
    createPartnerCampaign,
    updatePartnerCampaign,
    deletePartnerCampaign,
    createMarketingCampaign,
    updateMarketingCampaign,
    deleteMarketingCampaign,
    submitPromoCodeRequest,
    updatePromoCodeRequest,
    createContactInquiry,
    updateContactInquiry,
    deleteContactInquiry,
    createCategory,
    updateCategory,
    deleteCategory,
    createFilter,
    updateFilter,
    deleteFilter,
    register,
    changePassword,
    updateProfile,
    needsPasswordChange,
    approveAdvertisement,
    rejectAdvertisement,
    changeAdvertisementStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
